import { Editor, Transforms, type PathRef, type Path } from 'slate';
import { getCurrentListItems } from '../utils';
import { isListItem, isCheckboxListItem } from '../guards';
import ListDepthUtils from '../ListDepthUtils';
import type {
  AhaSlateEditor,
  EventHandler,
  ListItemElement,
} from '../../../types/aha-editor';
import EditorUtils from '../../../AhaEditorUtils/EditorUtils';

export const cleanupNewListItem = (editor: AhaSlateEditor, path: Path) => {
  const result = Editor.node(editor, path);
  if (!result) {
    return;
  }

  const [listItem] = result;
  const itemProps: { list_checked?: boolean } = {};

  if (isCheckboxListItem(listItem)) {
    itemProps.list_checked = false;
  }

  Transforms.setNodes(editor, { ...itemProps }, { at: path });
};

// When the user presses Enter within a List item, the intended behavior is as follows:
// 1. Shift + Enter creates a line break within the list item.
// 2. Enter creates a new list item. If the item is within a checklist, the newly created
//    list item must be unchecked.
const onEnter: EventHandler<KeyboardEvent> = (event, ahaEditor) => {
  const { editor } = ahaEditor;
  const listItems = getCurrentListItems(editor);
  if (!listItems.length) return undefined;

  event.preventDefault();
  if (event.shiftKey) {
    return undefined;
  }

  const itemsWithPathRef: [ListItemElement, PathRef][] = listItems.map(
    ([item, path]) => [item, Editor.pathRef(editor, path)]
  );

  for (const listItemEntry of itemsWithPathRef) {
    const [listItem, path] = listItemEntry;
    if (!path.current) {
      continue;
    }

    const isEmpty = EditorUtils.isNodeEmpty(editor, listItem);

    // Unindent if the current element is empty
    if (isEmpty) {
      ListDepthUtils.decreaseItemDepth(editor, [listItem, path.current]);
    } else {
      Transforms.splitNodes(editor, {
        always: true,
        match: isListItem,
      });
      cleanupNewListItem(editor, path.current);
    }
  }

  itemsWithPathRef.forEach(([, pathRef]) => pathRef.unref());
  return true;
};

export default onEnter;
